/**
 * @file MyTodosList.tsx
 * @brief The grouped "My to-dos" rows, shared by Home and the `/todos` page.
 *
 * @details
 * The phone's rows (DES 12-10-2026):
 * - Small group labels: Overdue, Today, Tomorrow, This week, Later, No due
 *   date (as the server groups them; empty groups are not sent).
 * - A tick circle on the left; only a row with `canTick` gets a button.
 * - Line 1: the task, a small Lucide `Sparkles` after an AI to-do's text
 *   (a manual one gets no mark), the due date on the right; overdue dates in
 *   the destructive red, none for Today / Tomorrow.
 * - Line 2, small and muted: the meeting title (· "Follow up" / the; nothing
 *   for my own, DES 14-10-2026)
 *   assignee.
 * - Clicking a row opens the meeting on its to-dos tab (`?tab=todos`).
 * - To-do-only rows (`access === 'todo'`, DES 12-10-2026: a colleague given a
 *   to-do in a meeting they cannot see gets that to-do only): line 2 reads
 *   meeting · from <name> · meeting date with a Lock, and the row opens a
 *   small to-do card (task, due, meeting, "From …", tick, Close), never the
 *   meeting, which would 404.
 *
 * **Ticking** is optimistic: the circle fills and the text is struck at once,
 * and the row leaves ~1.5s later, when the list refetches. An "Undo" toast
 * stays ~4s. A failed tick rolls the row back and says so. In the Done tab
 * the same click unticks.
 *
 * @author DES
 * @date 12-10-2026
 */

import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { backStateHere } from "@/shared/navigation/backState";
import { useQueryClient } from "@tanstack/react-query";
import { Circle, CircleCheck, Lock, Sparkles } from "lucide-react";
import { ToastAction } from "@/components/ui/Toast";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/DropdownMenu";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { isPastHistory } from "@/shared/history/historyLock";
import { useHistoryFrom } from "@/shared/history/useHistoryFrom";
import { myTodosKeys, useTickMyTodo, type MyTodoItem, type MyTodosResponse } from "./myTodosApi";
import { CategoryHeadingMenu, MoveTodoMenu, TodoGroupHeading } from "./TodoGroupingControls";
import { useTodoCategories } from "./todoGroupingApi";
import { groupTodos, type ViewFacts } from "./todoViews";
import {
  dueDateText,
  dueDateTextByMeeting,
  fromText,
  groupByMeeting,
  groupLabel,
  meetingDateText,
  whoText,
  type TodoGrouping,
} from "./myTodosLogic";

/** How long a ticked row stays, struck through, before it leaves. */
const LEAVE_MS = 1500;
/** How long the Undo toast stays. */
const UNDO_MS = 4000;

const rowKey = (t: Pick<MyTodoItem, "meetingId" | "todoId">) => `${t.meetingId}:${t.todoId}`;

/**
 * The optimistic tick: per row, the state shown until the list refetches,
 * and the rows that have left. One instance per list.
 */
function useOptimisticTicks() {
  const qc = useQueryClient();
  const tick = useTickMyTodo();
  const [shown, setShown] = useState<Record<string, boolean>>({});
  const [gone, setGone] = useState<Set<string>>(() => new Set());
  const timers = useRef(new Map<string, number>());

  useEffect(() => {
    const all = timers.current;
    return () => all.forEach((t) => window.clearTimeout(t));
  }, []);

  const forget = useCallback((key: string) => {
    setShown((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setGone((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const stopTimer = (key: string) => {
    const t = timers.current.get(key);
    if (t !== undefined) window.clearTimeout(t);
    timers.current.delete(key);
  };

  /** Refetch the lists, then drop the local state: the server's rows rule. */
  const refresh = useCallback(
    async (key: string) => {
      await qc.invalidateQueries({ queryKey: myTodosKeys.all });
      forget(key);
    },
    [qc, forget],
  );

  const setRow = useCallback(
    (item: MyTodoItem, completed: boolean, withUndo: boolean) => {
      const key = rowKey(item);
      stopTimer(key);
      setShown((prev) => ({ ...prev, [key]: completed }));
      setGone((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });

      // The row leaves after LEAVE_MS, or once the PATCH lands if later.
      let saved = false;
      let left = false;
      const leave = () => {
        left = true;
        setGone((prev) => new Set(prev).add(key));
        if (saved) void refresh(key);
      };
      if (withUndo) timers.current.set(key, window.setTimeout(leave, LEAVE_MS));

      tick
        .mutateAsync({ meetingId: item.meetingId, todoId: item.todoId, completed })
        .then(() => {
          saved = true;
          // An undo puts the row back where it was: refresh at once.
          if (left || !withUndo) void refresh(key);
        })
        .catch(() => {
          stopTimer(key);
          forget(key);
          toast({
            variant: "destructive",
            title: withUndo ? "Could not update this to-do" : "Could not undo",
            description: "Please try again.",
          });
        });

      if (withUndo) {
        toast({
          title: completed ? "To-do done" : "Marked as not done",
          duration: UNDO_MS,
          action: (
            <ToastAction altText="Undo" onClick={() => setRow(item, !completed, false)}>
              Undo
            </ToastAction>
          ),
        });
      }
    },
    [tick, refresh, forget],
  );

  return {
    /** The state to draw: the optimistic one, else the server's. */
    completedOf: (item: MyTodoItem) => shown[rowKey(item)] ?? item.completed,
    isGone: (item: MyTodoItem) => gone.has(rowKey(item)),
    toggle: (item: MyTodoItem) => setRow(item, !(shown[rowKey(item)] ?? item.completed), true),
  };
}

export function MyTodosList({
  data,
  meetingHref,
  backLabel,
  className,
  grouping = "date",
  collapsible = false,
}: {
  data: MyTodosResponse;
  /** "meeting" = one heading per meeting (DES 14-10-2026); "category" /
   *  "person" = F1a learned categories / owner (31-10-2026). */
  grouping?: TodoGrouping;
  /** Headings fold with a count (the My to-dos page; Home keeps its look). */
  collapsible?: boolean;
  /** The meeting detail's path in this shell, e.g. `/app/meetings/:id`. */
  meetingHref: (meetingId: string) => string;
  /** The meeting's Back returns here, labelled this (DES 13-10-2026). */
  backLabel: string;
  className?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const ticks = useOptimisticTicks();
  const openMeeting = (item: MyTodoItem) =>
    navigate(`${meetingHref(item.meetingId)}?tab=todos`, {
      state: backStateHere(location, backLabel),
    });
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const labelled = data.groups.some((g) => g.items.some((it) => it.category));
  // The viewer's own list (company, else personal): the move chip's choices
  // and whether category headings can be renamed / merged.
  const categories = useTodoCategories(null, labelled && (grouping === "category" || grouping === "person"));
  const categoryList = categories.data?.categories ?? [];

  if (grouping === "category" || grouping === "person") {
    const items = data.groups.flatMap((g) => g.items.filter((it) => !ticks.isGone(it)));
    const facts = (it: MyTodoItem): ViewFacts => ({
      category: it.category,
      categoryId: it.categoryId,
      person: it.assigneeName || (it.role === "assignee" || it.role === "followUp" ? "You" : null),
    });
    const groups = groupTodos(items, grouping, facts);
    const chip = (item: MyTodoItem): ReactNode =>
      labelled && item.canTick && item.access === "meeting" ? (
        <MoveTodoMenu
          meetingId={item.meetingId}
          todoId={item.todoId}
          category={item.category}
          categoryId={item.categoryId}
          categories={categoryList}
        />
      ) : null;
    return (
      <div className={className}>
        {groups.map((g) => {
          const isCollapsed = collapsed.has(g.key);
          // Only a heading from the viewer's own list can be renamed here.
          const editable =
            g.kind === "category" && g.categoryId && categories.data?.canManage &&
            categoryList.some((c) => c.id === g.categoryId);
          return (
            <section key={g.key} className="mt-5 first:mt-0">
              <TodoGroupHeading
                className="mb-1 px-1"
                label={g.label}
                count={g.items.length}
                collapsed={isCollapsed}
                onToggle={() => toggleGroup(g.key)}
                menu={
                  editable ? (
                    <CategoryHeadingMenu categoryId={g.categoryId as string} name={g.label} categories={categoryList} />
                  ) : null
                }
              />
              {isCollapsed ? null : (
                <ul>
                  {g.items.map((item) => (
                    <li key={rowKey(item)}>
                      <MyTodoRow
                        item={item}
                        today={data.today ?? null}
                        completed={ticks.completedOf(item)}
                        onToggle={() => ticks.toggle(item)}
                        onOpen={() => openMeeting(item)}
                        chip={chip(item)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    );
  }

  if (grouping === "meeting") {
    // One heading per meeting: its name once, day and count, "n overdue" in
    // red; rows keep their due date on the right (DES 14-10-2026).
    const meetings = groupByMeeting(
      data.groups.map((g) => ({ items: g.items.filter((it) => !ticks.isGone(it)) })),
    );
    return (
      <div className={className}>
        {meetings.map((m) => (
          <section key={m.meetingId} className="mt-6 first:mt-0">
            {collapsible ? (
              <TodoGroupHeading
                className="mb-1 px-1"
                label={m.meetingTitle}
                count={m.items.length}
                collapsed={collapsed.has(`meeting:${m.meetingId}`)}
                onToggle={() => toggleGroup(`meeting:${m.meetingId}`)}
                meta={
                  <>
                    {meetingDateText(m.meetingDate, data.today ?? null) ? (
                      <span className="shrink-0 text-[13px] text-app-text-secondary">
                        · {meetingDateText(m.meetingDate, data.today ?? null)}
                      </span>
                    ) : null}
                    {m.overdue > 0 ? (
                      <span className="shrink-0 text-[13px] font-semibold text-destructive">{m.overdue} overdue</span>
                    ) : null}
                  </>
                }
              />
            ) : (
            /* A label, not a to-do (DES 14-10-2026): the date labels' 13
                semibold grey, with a faint line to the edge. */
            <h3 className="mb-1 flex min-w-0 items-center gap-2 px-1">
              <span className="truncate text-[13px] font-semibold leading-4 text-app-text-secondary">
                {m.meetingTitle}
              </span>
              <span className="shrink-0 text-[13px] text-app-text-secondary">
                {[meetingDateText(m.meetingDate, data.today ?? null), String(m.items.length)]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
              {m.overdue > 0 ? (
                <span className="shrink-0 text-[13px] font-semibold text-destructive">
                  {m.overdue} overdue
                </span>
              ) : null}
              <span aria-hidden className="h-px min-w-4 flex-1 bg-app-text/[0.14]" />
            </h3>
            )}
            {collapsible && collapsed.has(`meeting:${m.meetingId}`) ? null : (
            <ul>
              {m.items.map((item) => (
                <li key={rowKey(item)}>
                  <MyTodoRow
                    item={item}
                    today={data.today ?? null}
                    completed={ticks.completedOf(item)}
                    onToggle={() => ticks.toggle(item)}
                    onOpen={() => openMeeting(item)}
                    byMeeting
                  />
                </li>
              ))}
            </ul>
            )}
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className={className}>
      {data.groups.map((group) => {
        const items = group.items.filter((it) => !ticks.isGone(it));
        if (items.length === 0) return null;
        return (
          <section key={group.key} className="mt-4 first:mt-0">
            {collapsible ? (
              <TodoGroupHeading
                className="mb-1 px-1"
                label={groupLabel(group.key)}
                count={items.length}
                collapsed={collapsed.has(`due:${group.key}`)}
                onToggle={() => toggleGroup(`due:${group.key}`)}
                tone={group.key === "overdue" ? "overdue" : undefined}
              />
            ) : (
            /* The phone's group label: 13 semibold, Overdue in red (13-10-2026). */
            <h3
              className={cn(
                "mb-1 px-1 text-[13px] font-semibold leading-4",
                group.key === "overdue" ? "text-destructive" : "text-app-text-secondary",
              )}
            >
              {groupLabel(group.key)}
            </h3>
            )}
            {collapsible && collapsed.has(`due:${group.key}`) ? null : (
            <ul>
              {items.map((item) => (
                <li key={rowKey(item)}>
                  <MyTodoRow
                    item={item}
                    today={data.today ?? null}
                    completed={ticks.completedOf(item)}
                    onToggle={() => ticks.toggle(item)}
                    onOpen={() => openMeeting(item)}
                  />
                </li>
              ))}
            </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

function MyTodoRow({
  item,
  today,
  completed,
  onToggle,
  onOpen,
  byMeeting = false,
  chip,
}: {
  item: MyTodoItem;
  today: string | null;
  completed: boolean;
  onToggle: () => void;
  onOpen: () => void;
  /** F1a: the move-to-category chip, under line 2. */
  chip?: ReactNode;
  /** Under a meeting heading: no title on line 2, "Today" on the right. */
  byMeeting?: boolean;
}) {
  // To-do-only access (DES 12-10-2026): the user may see this to-do, never
  // its meeting, so the row opens a small card instead (the meeting would 404).
  const todoOnly = item.access === "todo";
  const [cardOpen, setCardOpen] = useState(false);
  // B39: the meeting is older than this user's history window; the row still
  // opens it, and the detail says why it cannot show.
  const historyFrom = useHistoryFrom();
  const pastHistory = !todoOnly && isPastHistory(item.meetingDate, historyFrom);
  const date = byMeeting
    ? dueDateTextByMeeting(item.dueDate, item.group, today)
    : dueDateText(item.dueDate, item.group, today);
  const meetingTitle = item.meetingTitle?.trim() || "Untitled meeting";
  const meetingDate = meetingDateText(item.meetingDate, today);
  const meta = todoOnly
    ? [byMeeting ? "" : meetingTitle, `from ${fromText(item.fromName)}`, byMeeting ? "" : meetingDate]
        .filter(Boolean)
        .join(" · ")
    : [byMeeting ? "" : meetingTitle, whoText(item.role, item.assigneeName)].filter(Boolean).join(" · ");
  const task = item.task?.trim() || "Untitled to-do";
  const open = todoOnly ? () => setCardOpen(true) : onOpen;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
    }
  };

  return (
    <div
      role={todoOnly ? "button" : "link"}
      tabIndex={0}
      onClick={open}
      onKeyDown={onKeyDown}
      className="relative flex cursor-pointer items-start gap-3 rounded-xl px-1 py-2.5 transition-colors hover:bg-black/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:bg-white/[0.04]"
    >
      <TickCircle canTick={item.canTick} completed={completed} onToggle={onToggle} />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-3">
          <p
            dir="auto"
            className={cn(
              "min-w-0 flex-1 text-[15px] leading-[22px] text-app-text transition-colors",
              completed && "text-app-text-secondary line-through decoration-app-text/30",
            )}
          >
            {task}
            {item.source === "ai" ? (
              <Sparkles
                className="mb-0.5 ml-1.5 inline h-3.5 w-3.5 text-app-text-secondary"
                strokeWidth={1.75}
                aria-label="Found by AI"
              />
            ) : null}
          </p>
          {date ? (
            <span
              className={cn(
                "shrink-0 text-[13px] font-medium",
                item.group === "overdue" && !completed
                  ? "text-destructive"
                  : "text-app-text-secondary",
              )}
            >
              {date}
            </span>
          ) : null}
        </div>
        {meta || todoOnly || pastHistory ? (
        <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[13px] text-app-text-secondary">
          {meta ? <span className="truncate">{meta}</span> : null}
          {pastHistory ? (
            <Lock
              className="h-3 w-3 shrink-0 text-muted-foreground"
              strokeWidth={1.75}
              aria-label="Older than your plan keeps"
            />
          ) : null}
          {todoOnly ? (
            <Lock
              className="h-3 w-3 shrink-0 text-muted-foreground"
              strokeWidth={1.75}
              aria-label="You can see this to-do only"
            />
          ) : null}
        </p>
        ) : null}
        {chip ? <div className="mt-1.5">{chip}</div> : null}
      </div>

      {todoOnly ? (
        // A card under the row, the ⋯ menu's surface (webpanel-searchable-
        // menu-list: menus, not centred dialogs). The trigger is an invisible
        // strip along the row's bottom, so the row itself stays a plain click.
        <DropdownMenu open={cardOpen} onOpenChange={setCardOpen}>
          <DropdownMenuTrigger asChild>
            <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-0" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="w-[min(22rem,calc(100vw-2rem))] p-4"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <TickCircle
                canTick={item.canTick}
                completed={completed}
                onToggle={() => {
                  onToggle();
                  setCardOpen(false);
                }}
              />
              <div className="min-w-0 flex-1">
                <p
                  dir="auto"
                  className={cn(
                    "text-[15px] font-semibold leading-[22px] text-app-text",
                    completed && "line-through decoration-app-text/30",
                  )}
                >
                  {task}
                </p>
                {item.dueDate ? (
                  <p
                    className={cn(
                      "mt-1 text-[13px] font-medium",
                      item.group === "overdue" && !completed
                        ? "text-destructive"
                        : "text-app-text-secondary",
                    )}
                  >
                    Due {groupDueText(item, today)}
                  </p>
                ) : null}
                <p className="mt-2 text-[13px] text-app-text-secondary">
                  {[meetingTitle, meetingDate].filter(Boolean).join(" · ")}
                </p>
                <p className="mt-0.5 text-[13px] text-app-text">From {fromText(item.fromName)}</p>
                <p className="mt-3 flex items-start gap-1.5 text-[12px] leading-4 text-muted-foreground">
                  <Lock className="mt-px h-3 w-3 shrink-0" strokeWidth={1.75} />
                  You were given this to-do. Ask {fromText(item.fromName)} if you need the meeting.
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setCardOpen(false)}
                className="rounded-full px-4 py-1.5 text-[14px] font-semibold text-app-text hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
              >
                Close
              </button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

/** "Today" / "Tomorrow" for those groups, else the row's date text. */
function groupDueText(item: MyTodoItem, today: string | null): string {
  if (item.group === "today") return "today";
  if (item.group === "tomorrow") return "tomorrow";
  return dueDateText(item.dueDate, item.group, today);
}

/** The meeting detail's tick: a stroke CircleCheck in brand when done, a
 *  plain Circle when open, 22px. A row without `canTick` gets no button. */
function TickCircle({
  canTick,
  completed,
  onToggle,
}: {
  canTick: boolean;
  completed: boolean;
  onToggle: () => void;
}) {
  const Glyph = completed ? CircleCheck : Circle;
  if (!canTick) {
    return (
      <span
        className="mt-px flex h-[22px] w-[22px] shrink-0 items-center justify-center text-muted-foreground/30"
        title="Only the meeting owner or this task's assignee can tick it"
      >
        <Glyph className="h-[22px] w-[22px]" strokeWidth={1.75} />
      </span>
    );
  }
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={completed}
      aria-label={completed ? "Mark as not done" : "Mark as done"}
      title={completed ? "Mark as not done" : "Mark as done"}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      onKeyDown={(e) => e.stopPropagation()}
      className={cn(
        "mt-px flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full transition-colors hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        completed ? "text-brand" : "text-muted-foreground/50",
      )}
    >
      <Glyph className="h-[22px] w-[22px]" strokeWidth={1.75} />
    </button>
  );
}
